import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { trackLead } from '../../lib/pixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { parent_name: '', email: '', phone: '', child_name: '', child_age: '' };
const FIELDS = [
  ['parent_name', 'Parent name', 'text', true],
  ['email', 'Email', 'email', true],
  ['phone', 'Phone', 'tel', false],
  ['child_name', "Child's name", 'text', false],
  ['child_age', "Child's age (months)", 'text', false],
];

export default function ProgramWaitlistForm({ program, cta = 'Join the priority waitlist' }) {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const tid = `${program}-waitlist`;

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading'); setError('');
    try {
      const r = await fetch(`${API}/program-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ program, ...form }),
      });
      if (!r.ok) throw new Error('Please check your details and try again.');
      setResult(await r.json());
      setStatus('success');
      trackLead({ content_name: `${program} waitlist` });
    } catch (err) {
      setStatus('error'); setError(err.message);
    }
  };

  if (status === 'success') {
    return (
      <div data-testid={`${tid}-success`} className="rounded-2xl bg-splash-teal/10 border border-splash-teal/30 p-6 flex gap-3">
        <CheckCircle2 className="w-6 h-6 text-splash-teal shrink-0" />
        <div>
          <div className="font-semibold text-splash-green">You&rsquo;re on the list &mdash; #{result?.position} in line!</div>
          <div className="text-sm text-splash-green/75">A member of our team will be in touch shortly. We&rsquo;ve also emailed you a link to track your live spot.</div>
          {result?.token && (
            <Link to={`/waitlist/status/${result.token}`} data-testid={`${tid}-track-link`} className="inline-flex mt-2 text-sm font-semibold text-splash-coralDark">Track my spot &rarr;</Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${tid}-form`} className="grid sm:grid-cols-2 gap-3">
      {FIELDS.map(([name, label, type, required]) => (
        <label key={name} className={`text-xs font-semibold text-splash-green/70 ${name === 'parent_name' ? 'sm:col-span-2' : ''}`}>
          {label}{required && ' *'}
          <input
            type={type}
            required={required}
            value={form[name]}
            onChange={(e) => setForm({ ...form, [name]: e.target.value })}
            data-testid={`${tid}-${name}`}
            className="mt-1 w-full rounded-xl border border-splash-sand bg-white px-4 py-3 text-sm text-splash-green focus:outline-none focus:ring-2 focus:ring-splash-teal"
          />
        </label>
      ))}
      <button
        type="submit"
        disabled={status === 'loading'}
        data-testid={`${tid}-submit`}
        className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark disabled:opacity-60 text-white px-6 py-3.5 font-semibold btn-transition"
      >
        {status === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />} {cta}
      </button>
      {error && <div data-testid={`${tid}-error`} className="sm:col-span-2 text-sm text-red-600">{error}</div>}
    </form>
  );
}
